import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryName, setEnquiryName] = useState("");
  const [enquiryContact, setEnquiryContact] = useState("");
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquirySent, setEnquirySent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      // Employees always land on /app — their normal workflow (tasks,
      // attendance, calls, chat) — even if they have `can_manage_marketplace`.
      // The catalog console is an OPTIONAL extra reachable via a link inside
      // /app; do NOT hijack their default view.
      nav(u.role === "owner" ? "/dashboard" : "/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      // Response is always 200 to avoid leaking account existence.
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotLoading(false);
    }
  };

  const openEnquiry = () => {
    setEnquiryName("");
    setEnquiryContact("");
    setEnquiryMessage("");
    setEnquirySent(false);
    setEnquiryOpen(true);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setEnquiryLoading(true);
    try {
      await api.post("/sales/enquiry", {
        name: enquiryName.trim(),
        contact: enquiryContact.trim(),
        message: enquiryMessage.trim(),
        referrer: typeof window !== "undefined" ? window.location.href : "",
      });
      setEnquirySent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEnquiryLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:block relative bg-[#050914] overflow-hidden">
        <div className="absolute inset-0 grid-paper-dark opacity-70" />
        <div
          className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full"
          style={{
            background: "radial-gradient(closest-side, rgba(0,85,255,0.3), transparent 70%)",
          }}
        />
        <div className="relative p-10 h-full flex flex-col text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="login-brand">
            <img
              src="/logo.png"
              alt="Computer Care"
              className="w-9 h-9 rounded-md bg-white p-0.5 object-contain"
            />
            <span className="font-heading font-bold text-lg">Computer Care</span>
          </Link>
          <div className="mt-auto max-w-md">
            <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
              Owner dashboard
            </div>
            <h2 className="font-heading text-3xl mt-3 leading-tight">
              Route calls, record conversations, track your team — in one place.
            </h2>
            <div className="mt-6 mono text-xs text-white/50">
              Owners sign in with the email and password you set at signup.
              <br />
              Employees use the credentials shared by their owner.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-5"
          data-testid="login-form"
        >
          <div>
            <h1 className="font-heading text-2xl font-bold">Sign in</h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back. Enter your details below.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button
                type="button"
                onClick={openForgot}
                className="text-xs text-[#0055FF] hover:underline"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </button>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0055FF] hover:bg-[#0040CC] h-11"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-xs text-slate-400 text-center">
            This workspace is invite-only. Ask the owner to add you if you
            don&apos;t have an account.
          </p>

          {/* Sales contact — for prospects interested in buying DialFlow */}
          <div
            className="border-t border-slate-100 pt-4 space-y-3"
            data-testid="login-sales-contact"
          >
            <p className="mono text-[10px] uppercase tracking-widest text-slate-400 text-center">
              Interested in DialFlow? Talk to us
            </p>
            <div className="flex flex-col items-center gap-1.5 text-sm">
              <a
                href="tel:+919735937981"
                className="flex items-center gap-2 text-slate-700 hover:text-[#0055FF] transition-colors"
                data-testid="login-contact-phone-1"
              >
                <PhoneCall className="w-3.5 h-3.5 text-[#FF5000]" />
                +91 97359 37981
              </a>
              <a
                href="tel:+919933914444"
                className="flex items-center gap-2 text-slate-700 hover:text-[#0055FF] transition-colors"
                data-testid="login-contact-phone-2"
              >
                <PhoneCall className="w-3.5 h-3.5 text-[#FF5000]" />
                +91 99339 14444
              </a>
              <a
                href="mailto:ccs.bud@gmail.com"
                className="flex items-center gap-2 text-slate-700 hover:text-[#0055FF] transition-colors"
                data-testid="login-contact-email"
              >
                <Mail className="w-3.5 h-3.5 text-[#FF5000]" />
                ccs.bud@gmail.com
              </a>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={openEnquiry}
              className="w-full border-[#0055FF]/30 text-[#0055FF] hover:bg-[#0055FF]/5 hover:text-[#0055FF] hover:border-[#0055FF] gap-2"
              data-testid="login-open-enquiry"
            >
              <MessageSquare className="w-4 h-4" />
              Send us a quick enquiry
            </Button>
          </div>
        </form>
      </div>

      {/* Forgot-password magic-link modal */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-sm" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Enter the email on your DialFlow account. We&apos;ll send you a
              one-time magic link (valid for 60 minutes) to set a new password.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 space-y-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                If <span className="font-medium">{forgotEmail}</span> matches an
                active account, a reset link is on its way. Check your inbox
                (and spam) in the next couple of minutes.
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  className="w-full bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="forgot-close"
                >
                  Got it
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="forgotEmail">Email</Label>
                <Input
                  id="forgotEmail"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="forgot-submit"
                >
                  {forgotLoading ? "Sending…" : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Sales enquiry dialog — emails the DialFlow inbox */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="max-w-sm" data-testid="enquiry-dialog">
          <DialogHeader>
            <DialogTitle>Tell us a little about you</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              We&apos;ll get back within a business day. Or call{" "}
              <a href="tel:+919735937981" className="text-[#0055FF] hover:underline">
                +91 97359 37981
              </a>{" "}
              if it&apos;s urgent.
            </DialogDescription>
          </DialogHeader>
          {enquirySent ? (
            <div className="py-4 space-y-3">
              <div
                className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800"
                data-testid="enquiry-success"
              >
                Thanks <span className="font-medium">{enquiryName || "for reaching out"}</span> —
                your enquiry has landed in our inbox. We&apos;ll be in touch
                shortly.
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setEnquiryOpen(false)}
                  className="w-full bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="enquiry-close"
                >
                  Great, thanks
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitEnquiry} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="enquiryName">Your name</Label>
                <Input
                  id="enquiryName"
                  value={enquiryName}
                  onChange={(e) => setEnquiryName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={120}
                  autoFocus
                  data-testid="enquiry-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enquiryContact">Phone or email</Label>
                <Input
                  id="enquiryContact"
                  value={enquiryContact}
                  onChange={(e) => setEnquiryContact(e.target.value)}
                  placeholder="+91… or you@example.com"
                  required
                  minLength={3}
                  maxLength={200}
                  data-testid="enquiry-contact"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="enquiryMessage">How can we help? (optional)</Label>
                <Textarea
                  id="enquiryMessage"
                  value={enquiryMessage}
                  onChange={(e) => setEnquiryMessage(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Team size, industry, or what problem you're trying to solve…"
                  data-testid="enquiry-message"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEnquiryOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={enquiryLoading}
                  className="bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="enquiry-submit"
                >
                  {enquiryLoading ? "Sending…" : "Send enquiry"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
